import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { listRejectionReasons, type RejectionReasonOption } from '../../api/rejectionReasons.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { ApprovalLevels, personLabel } from '../../components/ApprovalLevels.js';
import { approveInstance, getInstance, rejectInstance, type WorkflowInstance } from '../../api/workflowApi.js';
import {
  AreaPanel,
  FieldWorkPanel,
  GpsReadingsTable,
  JobRecordPanel,
  SubmissionPhotos,
  type Submission,
  type SubmissionGps,
  type SubmissionPhoto,
} from './ReviewPanels.js';
import {
  reviewPath,
  vendorToolsOpen,
  type ReviewDetail,
  type ScoreRecord,
} from '../../../../shared/contracts/review-submission.js';

/**
 * M10 L10.02 — Quality Completion Review: the one screen every reviewer of a
 * painter's submission works on (owner, 25 Oct), opened from the BO Queue and,
 * for the vendor, from his Quality Reviews.
 *
 * Shows everything the system holds on the submission - the job, its dealer
 * and site, the field work, every photo in the viewer beside the list, the GPS
 * readings against the site and the area with every correction - and lets the
 * reader correct the area while the decision is his. What he decides comes
 * from the server: the vendor his review (score, approve, reject, return for
 * rework; reason from the M05 Rejection Reason Master, §15-16), every later
 * reviewer his level of the job's workflow. Tenant + actor are derived
 * server-side from the JWT.
 */
const RATINGS = [1, 2, 3, 4, 5] as const;

export function QualityReviewPage(): ReactNode {
  const { reviewId = '' } = useParams<{ reviewId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [review, setReview] = useState<ReviewDetail | null>(null);
  const [score, setScore] = useState<ScoreRecord | null>(null);
  const [reasons, setReasons] = useState<readonly RejectionReasonOption[]>([]);
  // Shown beside the picker: `error` would replace the whole review.
  const [reasonsError, setReasonsError] = useState<string | null>(null);
  // Null until read, and stays null when the read fails: a tile never shows a
  // count nobody counted.
  const [counts, setCounts] = useState<{ photos: number | null; gps: number | null; materials: number | null }>(
    { photos: null, gps: null, materials: null },
  );
  // Why the score or an evidence count could not be read. Apart from `error`,
  // which replaces the whole review.
  const [sideError, setSideError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The painter's submission, as every reviewer reads it.
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [photos, setPhotos] = useState<readonly SubmissionPhoto[]>([]);
  const [gps, setGps] = useState<readonly SubmissionGps[]>([]);
  const [instance, setInstance] = useState<WorkflowInstance | null>(null);
  const navigate = useNavigate();
  // Opened from the BO Queue or from the vendor's Quality Reviews; back goes
  // to where the reader came from.
  const fromBoQueue = useLocation().pathname.startsWith('/bo/queue');
  const backTo = fromBoQueue ? '/bo/queue' : '/quality/queue';

  // score form
  const [qualityRating, setQualityRating] = useState(5);
  const [photoRating, setPhotoRating] = useState(5);
  const [gpsRating, setGpsRating] = useState(5);
  const [areaRating, setAreaRating] = useState(5);
  // decision form
  const [remarks, setRemarks] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  // rework form
  const [reworkInstructions, setReworkInstructions] = useState('');
  const [reworkDeadline, setReworkDeadline] = useState('');
  const [reworkGuidance, setReworkGuidance] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await apiRequest<ReviewDetail>(reviewPath(reviewId), { token });
      setReview(r);
      setError(null);
      const failures: string[] = [];
      try {
        const s = await apiRequest<ScoreRecord | null>(reviewPath(reviewId, 'score'), { token });
        setScore(s ?? null);
      } catch (e) {
        // An unread score is not "not scored yet".
        setScore(null);
        failures.push((e as Error).message);
      }
      const countOf = async (seg: string): Promise<number | null> => {
        try {
          const rows = await apiRequest<readonly unknown[]>(reviewPath(reviewId, seg), { token });
          return Array.isArray(rows) ? rows.length : 0;
        } catch (e) {
          failures.push((e as Error).message);
          return null;
        }
      };
      const listOf = async <T,>(seg: string): Promise<readonly T[] | null> => {
        try {
          const rows = await apiRequest<readonly T[]>(reviewPath(reviewId, seg), { token });
          return Array.isArray(rows) ? rows : [];
        } catch (e) {
          failures.push((e as Error).message);
          return null;
        }
      };
      const [sub, photoRows, gpsRows, materials] = await Promise.all([
        apiRequest<Submission>(reviewPath(reviewId, 'submission'), { token })
          .catch((e) => { failures.push((e as Error).message); return null; }),
        listOf<SubmissionPhoto>('photos'), listOf<SubmissionGps>('gps'), countOf('materials'),
      ]);
      setSubmission(sub);
      setPhotos(photoRows ?? []);
      setGps(gpsRows ?? []);
      setCounts({ photos: photoRows ? photoRows.length : null, gps: gpsRows ? gpsRows.length : null, materials });
      // A later reviewer decides his level on the job's workflow: its levels
      // and trail are read from the workflow itself.
      if (sub?.myDecision?.decidesOn === 'WORKFLOW') {
        try { setInstance(await getInstance(token, sub.myDecision.instanceId)); }
        catch (e) { failures.push((e as Error).message); }
      } else {
        setInstance(null);
      }
      setSideError(failures.length > 0 ? [...new Set(failures)].join('; ') : null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [reviewId, token]);

  useEffect(() => { void load(); }, [load]);

  // The vendor's own review tools: while his level is the reader's, or - for a
  // job that runs on no workflow - while the review is still open with him.
  const decidesOn = submission?.myDecision?.decidesOn ?? null;
  const vendorTools = review !== null && submission !== null && vendorToolsOpen(review, submission);

  // The master's reasons serve the vendor's reject only; a later reviewer
  // decides his level with remarks, so they are not asked for on his behalf.
  useEffect(() => {
    if (!vendorTools) return undefined;
    let alive = true;
    listRejectionReasons(token, 'M10')
      .then((rows) => { if (alive) setReasons(rows); })
      .catch((e) => { if (alive) setReasonsError((e as Error).message); });
    return () => { alive = false; };
  }, [token, vendorTools]);

  const onSaveScore = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      await apiRequest(reviewPath(reviewId, 'score'), {
        method: 'POST', token,
        body: { qualityRating, photoRating, gpsRating, areaRating },
      });
      await load();
    } catch (err) { setActionError((err as Error).message); }
    finally { setBusy(false); }
  }, [reviewId, token, qualityRating, photoRating, gpsRating, areaRating, load]);

  const doAction = useCallback(async (path: string, body?: unknown) => {
    setBusy(true);
    setActionError(null);
    try {
      await apiRequest(reviewPath(reviewId, path), { method: 'POST', token, body });
      setRemarks(''); setReasonCode('');
      await load();
    } catch (err) { setActionError((err as Error).message); }
    finally { setBusy(false); }
  }, [reviewId, token, load]);

  const onSubmitRework = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      const guidanceImageUris = reworkGuidance
        .split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
      await apiRequest(reviewPath(reviewId, 'rework'), {
        method: 'POST', token,
        body: {
          correctionInstructions: reworkInstructions.trim(),
          deadlineAt: reworkDeadline ? new Date(reworkDeadline).toISOString() : undefined,
          guidanceImageUris: guidanceImageUris.length ? guidanceImageUris : undefined,
        },
      });
      setReworkInstructions(''); setReworkDeadline(''); setReworkGuidance('');
      await load();
    } catch (err) { setActionError((err as Error).message); }
    finally { setBusy(false); }
  }, [reviewId, token, reworkInstructions, reworkDeadline, reworkGuidance, load]);

  const selectedReason = reasons.find((r) => r.reasonCode === reasonCode) ?? null;
  const rejectDisabled = busy || !reasonCode.trim() || (!!selectedReason?.requiresRemarks && !remarks.trim());

  // A later reviewer decides his level of the job's workflow; the workflow
  // then takes the job to the next level, or ends it as the designer set.
  const decideLevel = useCallback(async (action: 'approve' | 'reject') => {
    const instanceId = submission?.myDecision?.instanceId;
    if (!instanceId) return;
    if (action === 'reject' && !remarks.trim()) { setActionError(t('quality.decide.remarksRequired')); return; }
    setBusy(true);
    setActionError(null);
    try {
      if (action === 'approve') await approveInstance(token, instanceId, remarks.trim() || undefined);
      else await rejectInstance(token, instanceId, remarks.trim());
      navigate(backTo);
    } catch (err) { setActionError((err as Error).message); }
    finally { setBusy(false); }
  }, [submission, remarks, token, navigate, backTo, t]);

  if (error) return <main aria-label="quality-review"><p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p></main>;
  if (!review) return <main aria-label="quality-review"><p>{t('common.loading')}</p></main>;

  const link = (seg: string) => `/quality/review/${review.reviewId}/${seg}`;
  const counted = (n: number | null) => (n === null ? '—' : n);

  return (
    <main aria-label="quality-review">
      <p style={{ marginBottom: 8 }}><Link to={backTo}>{t(fromBoQueue ? 'quality.review.backBoQueue' : 'quality.review.backQueue')}</Link></p>
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>{t('quality.review.title', { job: review.jobNumber ?? '—' })}</h1>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16, fontSize: 13 }}>
        {t('quality.review.subtitle', { job: review.jobNumber ?? '—', vendor: personLabel(review.vendorName, review.vendorCode) ?? '—', painter: personLabel(review.painterName, review.painterCode) ?? '—', cycle: review.cycleNo })}
      </p>

      <section aria-label="review-summary" style={{ marginBottom: 20 }}>
        <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 16px', fontSize: 13 }}>
          <dt style={{ fontWeight: 600 }}>{t('quality.common.status')}</dt><dd data-testid="review-status">{review.reviewStatus}</dd>
          <dt style={{ fontWeight: 600 }}>{t('quality.col.sla')}</dt><dd>{at(review.slaDeadlineAt)}</dd>
          <dt style={{ fontWeight: 600 }}>{t('quality.review.started')}</dt><dd>{review.startedAt ? at(review.startedAt) : '—'}</dd>
          <dt style={{ fontWeight: 600 }}>{t('quality.review.decided')}</dt><dd>{review.decidedAt ? at(review.decidedAt) : '—'}</dd>
        </dl>
      </section>

      {sideError && <p role="alert" data-testid="evidence-error" style={{ color: 'var(--color-danger)' }}>{sideError}</p>}

      {/* Everything the system holds on the submission, the same for every reviewer. */}
      <JobRecordPanel jobRequestId={review.jobRequestId} />
      {submission && <FieldWorkPanel submission={submission} />}
      <section aria-label="photos" style={{ marginBottom: 24 }}>
        <SubmissionPhotos rows={photos} />
      </section>
      <section aria-label="gps" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, margin: '0 0 8px' }}>{t('quality.gps.recorded')}</h2>
        <GpsReadingsTable rows={gps} />
      </section>
      {submission && <AreaPanel submission={submission} />}

      {decidesOn === 'WORKFLOW' && (
        <section aria-label="level-decision" style={{ borderTop: '1px solid var(--color-border-default)', paddingTop: 16 }}>
          <h2 style={{ fontSize: 16, marginBottom: 8 }}>{t('quality.decide.title')}</h2>
          {instance?.stepTrail ? <ApprovalLevels steps={instance.stepTrail} /> : null}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 480 }}>
            <label htmlFor="level-remarks" style={{ fontSize: 12 }}>{t('quality.review.remarks')}</label>
            <textarea id="level-remarks" data-testid="level-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2}
                      style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <Button type="button" data-testid="approve-level" disabled={busy} variant="primary"
                      onClick={() => void decideLevel('approve')}>
                {t('quality.common.approve')}
              </Button>
              <Button type="button" data-testid="reject-level" disabled={busy || !remarks.trim()} variant="danger"
                      onClick={() => void decideLevel('reject')}>
                {t('quality.common.reject')}
              </Button>
            </div>
          </div>
        </section>
      )}

      {vendorTools && (<>
      {/* §8 completion evidence — deep links into the vendor's dedicated validation screens */}
      <section aria-label="evidence-links" style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>{t('quality.review.evidenceTitle')}</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Link data-testid="link-photos" to={link('photos')} style={tileStyle}>{t('quality.review.tile.photos', { n: counted(counts.photos) })}</Link>
          <Link data-testid="link-gps" to={link('gps')} style={tileStyle}>{t('quality.review.tile.gps', { n: counted(counts.gps) })}</Link>
          <Link data-testid="link-area" to={link('area')} style={tileStyle}>{t('quality.review.tile.area')}</Link>
          <Link data-testid="link-material" to={link('material')} style={tileStyle}>{t('quality.review.tile.material', { n: counted(counts.materials) })}</Link>
          <Link data-testid="link-evidence" to={link('evidence')} style={tileStyle}>{t('quality.review.tile.evidence')}</Link>
          <Link data-testid="link-performance" to={link('performance')} style={tileStyle}>{t('quality.review.tile.performance')}</Link>
          <Link data-testid="link-audit" to={link('audit')} style={tileStyle}>{t('quality.review.tile.audit')}</Link>
        </div>
      </section>

      {review.reviewStatus === 'PENDING' && (
        <Button type="button" data-testid="start-review" disabled={busy} onClick={() => void doAction('start')} variant="primary" style={{ marginBottom: 20 }}>
          {t('quality.review.start')}
        </Button>
      )}

      <form onSubmit={onSaveScore} aria-label="score-form" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>{t('quality.review.scorecard')}</h2>
        {([
          ['Quality', qualityRating, setQualityRating, 'quality'],
          ['Photo', photoRating, setPhotoRating, 'photo'],
          ['GPS', gpsRating, setGpsRating, 'gps'],
          ['Area', areaRating, setAreaRating, 'area'],
        ] as const).map(([, val, setVal, key]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <label htmlFor={`score-${key}`} style={{ width: 80, fontSize: 13 }}>{t(`quality.review.score.${key}`)}</label>
            <select id={`score-${key}`} aria-label={`${key}-rating`} value={val}
                    onChange={(e) => setVal(Number(e.target.value))}
                    style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
              {RATINGS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        ))}
        {score && (
          <p data-testid="current-score" style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            {t('quality.review.recorded', { q: score.qualityRating, p: score.photoRating, g: score.gpsRating, a: score.areaRating })}
          </p>
        )}
        {score && (score.overallScore !== undefined && score.overallScore !== null) && (
          <p data-testid="overall-score" style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>
            {t('quality.review.overall', { score: score.overallScore })}
            {score.passThreshold != null ? t('quality.review.threshold', { threshold: score.passThreshold }) : ''}
            {' — '}
            <span data-testid="overall-verdict" style={{ color: score.passed ? 'var(--color-success)' : 'var(--color-danger)' }}>
              {score.passed ? t('quality.review.pass') : t('quality.review.fail')}
            </span>
          </p>
        )}
        <Button type="submit" data-testid="save-score" disabled={busy} variant="primary" style={{ marginTop: 8 }}>
          {busy ? t('quality.common.saving') : t('quality.review.saveScore')}
        </Button>
      </form>

      <section aria-label="decision" style={{ borderTop: '1px solid var(--color-border-default)', paddingTop: 16 }}>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>{t('quality.common.decision')}</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 480 }}>
          <label htmlFor="dec-remarks" style={{ fontSize: 12 }}>{t('quality.review.remarks')}</label>
          <textarea id="dec-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2}
                    style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
          <label htmlFor="dec-reason" style={{ fontSize: 12 }}>{t('quality.review.reason')}</label>
          <select id="dec-reason" aria-label="reject-reason-select" data-testid="reject-reason-select"
                  value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}
                  style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
            <option value="">{t('quality.review.selectReason')}</option>
            {reasons.map((r) => (
              <option key={r.reasonId} value={r.reasonCode}>{r.reasonDescription}</option>
            ))}
          </select>
          {reasonsError && <p role="alert" style={{ color: 'var(--color-danger)' }}>{reasonsError}</p>}
          {selectedReason?.requiresRemarks && (
            <p data-testid="reason-requires-remarks" style={{ fontSize: 11, color: 'var(--color-danger)' }}>
              {t('quality.review.requiresRemarks')}
            </p>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button type="button" data-testid="approve-review" disabled={busy}
                    onClick={() => void doAction('approve', { remarks: remarks.trim() || undefined })}
                    variant="primary">
              {t('quality.common.approve')}
            </Button>
            <Button type="button" data-testid="reject-review" disabled={rejectDisabled}
                    onClick={() => void doAction('reject', { reasonCode: reasonCode.trim(), remarks: remarks.trim() })}
                    variant="danger">
              {t('quality.common.reject')}
            </Button>
          </div>
        </div>
      </section>

      {/* §16 — return the job to the painter for rework with deadline + guidance */}
      <form onSubmit={onSubmitRework} aria-label="rework-form" style={{ borderTop: '1px solid var(--color-border-default)', paddingTop: 16, marginTop: 16, maxWidth: 480 }}>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>{t('quality.review.reworkTitle')}</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label htmlFor="rework-instructions" style={{ fontSize: 12 }}>{t('quality.review.correctionInstructions')}</label>
          <textarea id="rework-instructions" data-testid="rework-instructions" value={reworkInstructions}
                    onChange={(e) => setReworkInstructions(e.target.value)} rows={2} required
                    style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
          <label htmlFor="rework-deadline" style={{ fontSize: 12 }}>{t('quality.review.reworkDeadline')}</label>
          <DateInput id="rework-deadline" data-testid="rework-deadline" type="datetime-local" value={reworkDeadline}
                 onValueChange={(nextValue) => setReworkDeadline(nextValue)}
                 style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
          <label htmlFor="rework-guidance" style={{ fontSize: 12 }}>{t('quality.review.guidance')}</label>
          <textarea id="rework-guidance" data-testid="rework-guidance" value={reworkGuidance}
                    onChange={(e) => setReworkGuidance(e.target.value)} rows={2}
                    style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
          <Button type="submit" data-testid="submit-rework" disabled={busy || !reworkInstructions.trim()} variant="primary" style={{ alignSelf: 'flex-start' }}>
            {busy ? t('quality.review.submitting') : t('quality.review.returnRework')}
          </Button>
        </div>
      </form>
      </>)}

      {actionError && <p role="alert" style={{ color: 'var(--color-danger)', marginTop: 12 }}>{actionError}</p>}
    </main>
  );
}

const tileStyle = {
  padding: '8px 12px',
  borderRadius: 6,
  border: '1px solid var(--color-border-default)',
  background: 'var(--color-bg-muted)',
  fontSize: 13,
  textDecoration: 'none',
  color: 'var(--color-text-link)',
} as const;
